import { IShipmentPrice } from "@/types/apiResponse.types";
import { ICalculatorDetails } from "../types";
import { TransportationMode, TransportationPriceType } from "@/enums";

type Props = {
  values: ICalculatorDetails;
  shipmentPricesData: IShipmentPrice;
  isBuyForYou: boolean;
};

//  calculate transportation price
const calculateTransportationPrice = ({
  fromCountry,
  selectedDeliveryTypeId,
  deliveryPrice,
}: {
  fromCountry: number;
  selectedDeliveryTypeId: TransportationMode;
  deliveryPrice: IShipmentPrice["deliveryPrice"];
}) => {
  return deliveryPrice.fromCountryId[fromCountry][
    selectedDeliveryTypeId === TransportationMode.AIR
      ? TransportationPriceType.AIR
      : selectedDeliveryTypeId === TransportationMode.SEA
        ? TransportationPriceType.SEA
        : selectedDeliveryTypeId === TransportationMode.ECONOMY
          ? TransportationPriceType.SEA_ECONOMY
          : null
  ];
};

// calculate minimum weight
const calculateMinWeight = (
  weight: number,
  minWeightForCalculateKg: number,
) => {
  return Number(weight) < minWeightForCalculateKg
    ? minWeightForCalculateKg
    : Number(weight);
};

// calculate parcel duty
const calculateParcelDuty = (
  cost: number,
  customPriceLimitNational: number,
  shipmentPricesData: IShipmentPrice,
) => {
  const parcelCostInLocalCurrency =
    cost *
    shipmentPricesData?.customsEuroToUsdRateForCalculatingCustomsPaymentsInTocountry;

  const dutiableAmount =
    parcelCostInLocalCurrency - shipmentPricesData?.customPriceLimitNational;

  if (dutiableAmount <= 0) return 0;

  return (
    dutiableAmount * shipmentPricesData?.customsDutyRateInTocountry +
    dutiableAmount *
      (1 + shipmentPricesData?.customsDutyRateInTocountry) *
      shipmentPricesData?.customsVatInTocountry
  );
};

const calculateAdditionalServicesPrice = (
  additionalServices: number[],
  shipmentPricesData: IShipmentPrice,
  cost: number,
) => {
  const totalAdditionalServicesPrice = additionalServices.reduce(
    (acc, service) => {
      const shipmentServicestoArray = Object.values(
        shipmentPricesData?.shipmentServices,
      );
      const servicePrice =
        shipmentServicestoArray.find((item) => item.serviceId === service)
          ?.servicePriceUsd || 0;

      switch (service) {
        case 1:
          return acc + servicePrice * 2;
        case 113:
          return acc + (cost * servicePrice) / 100;
        default:
          return acc + servicePrice;
      }
    },
    0,
  );
  return parseFloat(totalAdditionalServicesPrice.toFixed(2));
};

// calculate all costs
export const calculateShipmentPriceURK = ({
  values: { details, fromCountry, selectedDeliveryTypeId, additionalServices },
  shipmentPricesData,
  isBuyForYou,
}: Props) => {
  let transportationPrice = 0;
  let additionalServicesPrice = 0;
  let minWeight = 0;
  let parcelDuty = 0;
  let totalCost = 0;
  let isBuyForYouCost = 0;

  const isValid = details.weight && details.cost;
  // details.length &&
  // details.width &&
  // details.height;

  if (isValid && shipmentPricesData) {
    // Round up weight to 100 grams (0.1 kg) precision
    const roundedWeight = Math.ceil(details.weight / 0.1) * 0.1;

    transportationPrice = calculateTransportationPrice({
      fromCountry,
      selectedDeliveryTypeId,
      deliveryPrice: shipmentPricesData?.deliveryPrice,
    });

    minWeight = calculateMinWeight(
      roundedWeight,
      shipmentPricesData?.minWeightForCalculateKg,
    );

    parcelDuty = calculateParcelDuty(
      details.cost,
      shipmentPricesData.customPriceLimitNational,
      shipmentPricesData,
    );

    if (additionalServices.length > 0) {
      additionalServicesPrice = calculateAdditionalServicesPrice(
        additionalServices,
        shipmentPricesData,
        details.cost,
      );
    }

    if (isBuyForYou) {
      isBuyForYouCost = parseFloat(
        Math.max(
          shipmentPricesData.minCommissionForBuyForBuyRequestUsd,
          details.cost *
            (shipmentPricesData.minCommissionPercentForBuyForBuyRequestUsd /
              100),
        ).toFixed(2),
      );
    }

    // Calculate transportationCost
    const transportationCost =
      selectedDeliveryTypeId === TransportationMode.AIR
        ? transportationPrice * Math.max(roundedWeight, minWeight)
        : selectedDeliveryTypeId === TransportationMode.SEA
          ? Math.max(
              transportationPrice * Math.max(roundedWeight, minWeight),
              shipmentPricesData?.deliveryPrice?.fromCountryId[fromCountry]
                ?.minCostForWeightForDeliverySeaUsd,
            )
          : selectedDeliveryTypeId === TransportationMode.ECONOMY
            ? Math.max(
                transportationPrice * Math.max(roundedWeight, minWeight),
                shipmentPricesData?.deliveryPrice?.fromCountryId[fromCountry]
                  ?.minCostForWeightForDeliverySeaEconomyUsd,
              )
            : 0;

    totalCost =
      transportationCost +
      // transportationPrice *
      //   parseFloat(Math.max(Number(details.weight), minWeight).toFixed(2)) +
      parcelDuty +
      additionalServicesPrice +
      isBuyForYouCost;
  }

  console.log("transportationPrice", transportationPrice);

  return {
    parcelDuty,
    transportationPrice,
    additionalServicesPrice,
    totalCost,
    minWeight,
    isBuyForYouCost,
  };
};
